import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Calendar, MapPin, IndianRupee, Loader2, ArrowLeft, Phone, Mail, Globe, User, Building2, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { STATUS_STYLES, bannerUrl, formatDate } from '../../components/tournament-hub/tournamentHubUtils';
import { API } from '../../utils/apiConfig';

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={`detail-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
      <div><span className="text-slate-500">{label}: </span><span className="font-medium text-slate-800">{value}</span></div>
    </div>
  );
};

export default function TournamentHubDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tournament-hub/${id}`)
      .then((r) => setT(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  if (notFound || !t) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-slate-500 gap-3">
      <Trophy className="w-10 h-10 opacity-40" /> Tournament not found
      <Button variant="outline" onClick={() => navigate('/hub')}>Back to Hub</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="tournament-hub-detail-page">
      <div className="relative">
        {t.banner_file_id ? (
          <img src={bannerUrl(t.banner_file_id)} alt={t.name} className="h-64 sm:h-80 w-full object-cover" />
        ) : (
          <div className="h-56 w-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center">
            <Trophy className="w-16 h-16 text-white/80" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <button onClick={() => navigate('/hub')} className="absolute top-4 left-4 bg-white/90 rounded-full p-2" data-testid="back-to-hub-btn">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white max-w-3xl mx-auto">
          <span className={`inline-block text-[10px] font-bold uppercase px-2 py-1 rounded-full mb-2 ${STATUS_STYLES[t.status] || 'bg-slate-100 text-slate-700'}`}>{t.status}</span>
          <h1 className="text-2xl sm:text-3xl font-bold" data-testid="detail-tournament-name">{t.name}</h1>
          <p className="text-amber-100 text-sm mt-1">{t.sport} · {t.tournament_type}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-4 relative space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-2 gap-3">
          <InfoRow icon={Calendar} label="Date" value={formatDate(t.tournament_date)} />
          <InfoRow icon={MapPin} label="Venue" value={t.venue} />
          <InfoRow icon={IndianRupee} label="Prize Pool" value={t.prize_pool ? `₹${Number(t.prize_pool).toLocaleString('en-IN')}` : ''} />
          <InfoRow icon={Trophy} label="Categories" value={t.categories} />
        </div>

        {t.details && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-2">About This Tournament</h3>
            <p className="text-sm text-slate-600 whitespace-pre-line" data-testid="detail-details-text">{t.details}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2">
          <h3 className="font-semibold text-slate-900 mb-1">Organiser</h3>
          <InfoRow icon={User} label="Organiser" value={t.organiser_name} />
          <InfoRow icon={Building2} label="Organisation" value={t.organisation_name} />
          <InfoRow icon={User} label="Contact Person" value={t.contact_person} />
          <InfoRow icon={Phone} label="Mobile" value={t.mobile_number} />
          <InfoRow icon={Mail} label="Email" value={t.email} />
          <InfoRow icon={Globe} label="Website" value={t.website} />
          <InfoRow icon={Building2} label="Organised & Managed By" value={t.organised_managed_by} />
        </div>

        {(t.main_sponsor || t.registration_partner || t.official_venue_partner || t.other_sponsors) && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2">
            <h3 className="font-semibold text-slate-900 mb-1">Partners & Sponsors</h3>
            <InfoRow icon={Trophy} label="Main Sponsor" value={t.main_sponsor} />
            <InfoRow icon={Trophy} label="Registration Partner" value={t.registration_partner} />
            <InfoRow icon={Trophy} label="Official Venue Partner" value={t.official_venue_partner} />
            <InfoRow icon={Trophy} label="Other Sponsors" value={t.other_sponsors} />
          </div>
        )}

        {t.banner_file_id && (
          <Button variant="outline" className="w-full" onClick={() => window.open(bannerUrl(t.banner_file_id), '_blank')} data-testid="view-banner-btn">
            <ExternalLink className="w-4 h-4 mr-2" /> View Banner (Full Size)
          </Button>
        )}
      </div>
    </div>
  );
}
